import React from "react";
import * as Mui from "@mui/material";
import {
  Close,
  CloseFullscreen,
  PolylineOutlined,
  StorageRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import {
  designColors as colors,
  designType,
  designShell,
  withDesignFont,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { GlobalFieldLabel } from "components/DesignSystem/sharedUiPatterns";
import { BulkReprocessEmployeePicker } from "./BulkReprocessEmployeePicker";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

// Same label + helper-text pattern Approve's Apply forms use above every
// field (see CompOff/Apply.js) — reused as-is here instead of a hand-rolled
// heading, so this panel stays visually identical to those forms and picks
// up any future change to that shared pattern automatically.
function Section({ title, subtitle, children }) {
  return (
    <Mui.Box
      component="section"
      sx={{
        py: 2,
        borderBottom: `1px solid ${colors.slate200}`,
        "&:last-child": { borderBottom: 0 },
      }}
    >
      <GlobalFieldLabel helperText={subtitle}>{title}</GlobalFieldLabel>
      {children}
    </Mui.Box>
  );
}

// The two starting points for a reprocess, shown as selectable cards. Only
// the card's look and wording live here; `value` is what the request sends.
const dataSourceOptions = [
  {
    value: "attendance",
    title: "Attendance Records",
    description:
      "Reprocess attendance using existing punch entry and attendance records.",
    icon: StorageRounded,
    iconColor: colors.teal,
  },
  {
    value: "biometric",
    title: "Raw Data",
    description:
      "Start from the original biometric and mobile events and reprocess the complete pipeline (Global Event → Attendance).",
    icon: PolylineOutlined,
    iconColor: "#2563EB",
  },
];

export function BulkReprocessPanel({
  companyId,
  users,
  selected,
  onSelectionChange,
  loading,
  error,
  onRetry,
  dataSource,
  onDataSourceChange,
  startDate,
  endDate,
  onDateChange,
  onSubmit,
  onClose,
  expanded,
  onToggleExpand,
  submitting,
  confirmOpen,
  onCancelConfirm,
  onConfirm,
}) {
  return (
    <Mui.ThemeProvider theme={withDesignFont}>
      <Mui.Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: colors.white,
          color: colors.slate900,
        }}
      >
        <Mui.Box
          sx={{
            px: 3,
            pt: expanded ? 2.5 : 3,
            pb: 2,
            flexShrink: 0,
            borderBottom: `1px solid ${colors.slate200}`,
          }}
        >
          <Mui.Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
              <Mui.Typography component="h2" sx={designType.panelTitle}>
                Reprocess Attendance
              </Mui.Typography>
              <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>
                Recalculate attendance and recreate shift mapping for the
                selected period and employees.
              </Mui.Typography>
            </Mui.Box>
            {/* Collapsed: bare tooltip icon buttons. Expanded: labeled buttons
              instead — matching Approve's Apply forms (see CompOff/Apply.js),
              which switch to text+icon SecondaryButtons once expanded rather
              than keeping unlabeled icons in the wider full-page view. */}
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              {expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Collapse"
                  onClick={onToggleExpand}
                >
                  <CloseFullscreen />
                </PanelHeaderIconButton>
              ) : (
                <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                  <Mui.IconButton
                    aria-label="Open full view"
                    onClick={onToggleExpand}
                    size="small"
                    sx={{
                      ...designShell.panelSoftIconButton,
                      color: colors.slate500,
                    }}
                  >
                    <OpenFullViewIcon fontSize="small" />
                  </Mui.IconButton>
                </Mui.Tooltip>
              )}
              {expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Close"
                  onClick={onClose}
                  disabled={submitting}
                >
                  <Close />
                </PanelHeaderIconButton>
              ) : (
                <Mui.IconButton
                  aria-label="Close reprocess attendance"
                  onClick={onClose}
                  disabled={submitting}
                  sx={designShell.panelHeaderIconButton}
                >
                  <Close fontSize="small" />
                </Mui.IconButton>
              )}
            </Mui.Stack>
          </Mui.Stack>
        </Mui.Box>
        <Mui.Box
          sx={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            px: { xs: 2, sm: 3 },
            pb: 2,
          }}
        >
          <Section
            title="Select Data Source"
            subtitle="Choose where reprocessing should begin."
          >
            <Mui.Box
              role="radiogroup"
              aria-label="Data source"
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {dataSourceOptions.map(
                ({ value, title, description, icon: SourceIcon, iconColor }) => {
                const active = dataSource === value;
                return (
                  <Mui.Box
                    component="label"
                    key={value}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 2,
                      height: "100%",
                      boxSizing: "border-box",
                      border: `1px solid ${active ? colors.teal : colors.slate200}`,
                      borderRadius: "10px",
                      bgcolor: active ? colors.tealTint : colors.white,
                      cursor: "pointer",
                      transition: "border-color 120ms, background-color 120ms",
                      "&:hover": {
                        borderColor: active ? colors.teal : colors.slate300,
                      },
                    }}
                  >
                    <Mui.Radio
                      checked={active}
                      onChange={() => onDataSourceChange(value)}
                      name="reprocess-data-source"
                      value={value}
                      size="small"
                      inputProps={{ "aria-label": title }}
                      sx={{
                        p: 0,
                        mt: 0.5,
                        color: colors.slate400,
                        "&.Mui-checked": { color: colors.teal },
                      }}
                    />
                    <Mui.Box sx={{ minWidth: 0 }}>
                      <SourceIcon
                        aria-hidden
                        sx={{ display: "block", fontSize: 32, color: iconColor }}
                      />
                      <Mui.Typography
                        sx={{
                          mt: 1,
                          fontSize: 15,
                          fontWeight: 600,
                          color: colors.slate900,
                        }}
                      >
                        {title}
                      </Mui.Typography>
                      <Mui.Typography
                        sx={{
                          mt: 0.5,
                          fontSize: 13,
                          lineHeight: 1.55,
                          color: colors.slate600,
                        }}
                      >
                        {description}
                      </Mui.Typography>
                    </Mui.Box>
                  </Mui.Box>
                );
                },
              )}
            </Mui.Box>
          </Section>
          {/* Both data sources take a date range. Attendance Records stops at
              yesterday; Raw Data may include today. */}
          <Section
            title="Select Date Period"
            subtitle="Choose the date range to reprocess attendance for."
          >
            <ApprovalDateRangeFilter
              key={dataSource}
              startDate={startDate?.format("YYYY-MM-DD")}
              endDate={endDate?.format("YYYY-MM-DD")}
              // Raw Data may include today: its list offers "Today", and its
              // presets run up to today instead of stopping at yesterday.
              showToday={dataSource === "biometric"}
              includeCurrentDate={dataSource === "biometric"}
              thisMonthToDate={dataSource === "biometric"}
              maxDate={
                dataSource === "attendance"
                  ? dayjs().subtract(1, "day").endOf("day").toDate()
                  : undefined
              }
              allowFutureDate={false}
              onChange={onDateChange}
              formFieldStyle
            />
          </Section>
          <Section
            title="Select Employees"
            subtitle="Select employees individually, by department, or using an advanced filter."
          >
            {/* No in-panel company picker — this screen is already scoped to the
              top bar's selected company (see initialCompanyId in BulkReprocess.js),
              so a second company control here would just be a redundant, easy-to-
              miss way to silently reprocess the wrong company's employees. */}
            {error && (
              <Mui.Button onClick={onRetry}>Retry loading employees</Mui.Button>
            )}
            <BulkReprocessEmployeePicker
              key={companyId || "no-company"}
              users={users}
              selected={selected}
              onChange={onSelectionChange}
              loading={loading}
              error={error}
              companySelected={Boolean(companyId)}
              expanded={expanded}
            />
          </Section>
        </Mui.Box>
        <Mui.Stack
          direction={{ xs: "column", md: expanded ? "row" : "column" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: expanded ? "center" : "stretch" }}
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderTop: `1px solid ${colors.slate200}`,
            bgcolor: colors.white,
          }}
        >
          <Mui.Alert severity="info" sx={{ flex: 1, py: 0, fontSize: 12 }}>
            This process will recalculate attendance and recreate shift mapping
            for the selected period and employees.
          </Mui.Alert>
          <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <SecondaryButton onClick={onClose} disabled={submitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={
                !companyId || !selected.length || loading || Boolean(error)
              }
              loading={submitting}
            >
              Reprocess
            </PrimaryButton>
          </Mui.Stack>
        </Mui.Stack>
      </Mui.Box>
      <Mui.Dialog
        open={confirmOpen}
        onClose={() => {
          if (!submitting) onCancelConfirm();
        }}
        aria-labelledby="reprocess-confirm-title"
        maxWidth="sm"
        fullWidth
      >
        <Mui.DialogTitle
          id="reprocess-confirm-title"
          sx={designType.panelTitle}
        >
          Reprocess Attendance
        </Mui.DialogTitle>
        <Mui.DialogContent>
          <Mui.DialogContentText>
            Reprocess attendance for {selected.length} selected employee
            {selected.length === 1 ? "" : "s"}{" "}
            {dayjs(startDate).isSame(dayjs(endDate), "day")
              ? `on ${dayjs(startDate).format("DD/MM/YYYY")}`
              : `from ${dayjs(startDate).format("DD/MM/YYYY")} to ${dayjs(endDate).format("DD/MM/YYYY")}`}
            ? Attendance and shift mapping will be recalculated.
          </Mui.DialogContentText>
        </Mui.DialogContent>
        <Mui.DialogActions sx={{ px: 3, pb: 3 }}>
          <SecondaryButton disabled={submitting} onClick={onCancelConfirm}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={onConfirm} loading={submitting}>
            Confirm Reprocess
          </PrimaryButton>
        </Mui.DialogActions>
      </Mui.Dialog>
    </Mui.ThemeProvider>
  );
}
